import { components } from "@certn/api/generated-schemas/client-portal";
import { Link, Stack, Text } from "@certn/cascadia";
import { LinkButton } from "@certn/cascadia-react";
import { formatDate, slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CredentialRequestStatus } from "./CredentialRequestStatus";
import { CancelRescreenModal } from "../../pages/person-profile/components/CancelRescreenModal";
import { EditRescreenModal } from "../../pages/person-profile/components/EditRescreenModal";
import { ScheduleRescreenModal } from "../../pages/person-profile/components/ScheduleRescreenModal";
import { formatAddress } from "../../utils/formatAddress";
import { LinkedCaseItem } from "../linked-cases/LinkedCaseItem";

interface CardContentProps {
  checks: string[];
  credentialRequest?: components["schemas"]["CredentialRequest"] | null;
  positionLocation?: components["schemas"]["PositionLocation"] | null;
  linkedCases:
    | components["schemas"]["LinkedCase"][]
    | components["schemas"]["PersonCaseDetailsLinkedCases"][];
  setIsCaseExpanded: (value: boolean) => void;
  isCaseExpanded: boolean;
  canCancelRescreen?: boolean;
  rescreenSchedule: string;
  caseId: string;
  refreshData: () => void;
}

export function CardContent({
  checks,
  credentialRequest,
  positionLocation,
  linkedCases,
  setIsCaseExpanded,
  isCaseExpanded,
  canCancelRescreen,
  rescreenSchedule,
  caseId,
  refreshData,
}: CardContentProps) {
  const { t } = useTranslation();
  const { clientPortalDisplayGuarantorsCoapplicants } = useFlags();
  const [showEditRescreenModal, setShowEditRescreenModal] = useState(false);
  const [showScheduleRescreenModal, setShowScheduleRescreenModal] =
    useState(false);
  const [showCancelRescreenModal, setShowCancelRescreenModal] = useState(false);

  return (
    <div id="case-details" className={styles.caseDetails}>
      <div className={styles.caseDetailsListItem}>
        <Text style="global/heading-small" testID="checks-list-label">
          {t("Checks")}
        </Text>
        {checks?.map((check, index) => {
          return (
            <Text
              style="global/body-secondary"
              testID="check-list-item"
              key={`${index}-${slugify(check)}`}
            >
              {check}
            </Text>
          );
        })}
      </div>
      {credentialRequest && (
        <Stack gap="space-2">
          <Stack orientation="horizontal" gap="space-2">
            <Text style="global/heading-small" testID="checks-list-label">
              {t("Credentials")}
            </Text>{" "}
            &#x2022;
            <CredentialRequestStatus credentialRequest={credentialRequest} />
            {credentialRequest.status === "CREDENTIALS_AVAILABLE" && (
              <Text>
                &#x2022;{" "}
                {t("Requested on {{date}}", {
                  date: formatDate(credentialRequest.created, "PP"),
                })}
              </Text>
            )}
          </Stack>
          {credentialRequest.requested_credentials.map((credential, index) => {
            return (
              <Text
                style="global/body-secondary"
                testID="credential-list-item"
                key={`${index}-${slugify(credential)}`}
              >
                {credential}
              </Text>
            );
          })}
        </Stack>
      )}
      {positionLocation && (
        <div className={styles.caseDetailsListItem}>
          <Text style="global/heading-small" testID="position-location-label">
            {t("Position location")}
          </Text>
          <Text style="global/body-secondary" testID="position-location">
            {formatAddress(positionLocation)}
          </Text>
        </div>
      )}
      {clientPortalDisplayGuarantorsCoapplicants &&
        linkedCases.length > 0 &&
        linkedCases.map((linkedCase, index) => {
          const coapplicantNumber = index + 1;
          return (
            <LinkedCaseItem
              key={linkedCase.short_id}
              linkedCase={linkedCase}
              linkedCaseNumber={
                linkedCases.length > 1 ? coapplicantNumber : undefined
              }
            />
          );
        })}
      {
        <>
          <div className={styles.caseDetailsListItem}>
            <Text style="global/heading-small" testID="rescreen-schedule-label">
              {t("Rescreen schedule")}
            </Text>
            <Text style="global/body-secondary" testID="rescreen-schedule">
              {rescreenSchedule || t("Do not rescreen")}{" "}
              <LinkButton
                textStyle="global/body-secondary"
                linkStyle="monochrome"
                testID="edit-rescreen-link-button"
                onPress={() => {
                  return rescreenSchedule
                    ? setShowEditRescreenModal(true)
                    : setShowScheduleRescreenModal(true);
                }}
              >
                {t("Edit")}
              </LinkButton>
            </Text>
          </div>
          {canCancelRescreen && (
            <div className={styles.caseDetailsListItem}>
              <LinkButton
                textStyle="global/body-secondary"
                linkStyle="monochrome"
                textColor="text-danger"
                testID="edit-rescreen-link-button"
                onPress={() => setShowCancelRescreenModal(true)}
              >
                {t("Cancel rescreening")}
              </LinkButton>
            </div>
          )}
          <CancelRescreenModal
            onClose={() => setShowCancelRescreenModal(false)}
            isOpen={showCancelRescreenModal}
            caseId={caseId}
            refreshData={refreshData}
          />
        </>
      }
      <Link
        textStyle="global/body-secondary"
        textColor="text-base"
        onPress={() => setIsCaseExpanded(!isCaseExpanded)}
        aria-controls="case-details"
        aria-expanded
        aria-label={t("Case Details")}
        testID="show-less-text"
      >
        {t("Show less")}
      </Link>
      <EditRescreenModal
        onClose={() => setShowEditRescreenModal(false)}
        isOpen={showEditRescreenModal}
        caseId={caseId}
        refreshData={refreshData}
        selectedKey={rescreenSchedule}
      />
      <ScheduleRescreenModal
        isOpen={showScheduleRescreenModal}
        caseId={caseId}
        onClose={() => setShowScheduleRescreenModal(false)}
        refreshData={refreshData}
      />
    </div>
  );
}

const styles = {
  caseDetails: css({
    borderTop: `solid 1px var(--border-base)`,
    padding: "var(--space-4)",
    display: "grid",
    gap: "var(--space-4)",
  }),
  caseDetailsListItem: css({
    display: "flex",
    flexDirection: "column",
  }),
};
